<template>
  <section class="marketing-tool-header">
    <div class="header-content">
      <div class="header-mark" aria-hidden="true">
        <v-icon>{{ icon }}</v-icon>
      </div>
      <div class="header-copy">
        <div class="header-eyebrow">{{ eyebrow }}</div>
        <h1>{{ title }}</h1>
        <p>{{ description }}</p>
      </div>
    </div>

    <div v-if="$slots.actions" class="header-actions">
      <slot name="actions"></slot>
    </div>
  </section>
</template>

<script>
export default {
  name: "MarketingToolHeader",
  props: {
    eyebrow: {
      type: String,
      default: "Marketing",
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "mdi-bullhorn-outline",
    },
  },
}
</script>

<style scoped>
.marketing-tool-header {
  position: relative;
  display: flex;
  min-height: 11.875rem;
  align-items: center;
  justify-content: space-between;
  gap: 1.75rem;
  padding: 2.125rem 2.375rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 1.5rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.09), transparent 48%), rgba(255, 255, 255, 0.035);
  box-shadow: 0 1.5rem 4.375rem rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.marketing-tool-header::after {
  position: absolute;
  right: 8%;
  bottom: -10.625rem;
  width: 16.875rem;
  height: 16.875rem;
  border-radius: 50%;
  background: rgba(0, 150, 64, 0.1);
  content: "";
  filter: blur(1.375rem);
  pointer-events: none;
}

.header-content,
.header-actions {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.header-content {
  min-width: 0;
  gap: 1.25rem;
}

.header-mark {
  display: grid;
  flex: 0 0 3.625rem;
  width: 3.625rem;
  height: 3.625rem;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 1.0625rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.header-mark .v-icon {
  color: currentColor;
  font-size: 1.8125rem;
}

.header-copy {
  min-width: 0;
}

.header-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.header-copy h1 {
  margin: 0.3125rem 0 0.4375rem;
  color: #ffffff;
  font-size: clamp(1.875rem, 4vw, 2.75rem);
  font-weight: 760;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.header-copy p {
  max-width: 43rem;
  margin: 0;
  color: #b8b8bc;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.header-actions {
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.625rem;
}

@media (max-width: 59.9375rem) {
  .marketing-tool-header {
    min-height: 10.75rem;
    padding: 1.75rem;
  }
}

@media (max-width: 37.4375rem) {
  .marketing-tool-header {
    min-height: auto;
    align-items: flex-start;
    flex-direction: column;
    gap: 1.125rem;
    padding: 1.3125rem 1.125rem;
    border-radius: 1.25rem;
  }

  .header-content {
    align-items: flex-start;
    gap: 0.75rem;
  }

  .header-mark {
    flex-basis: 2.875rem;
    width: 2.875rem;
    height: 2.875rem;
    border-radius: 0.875rem;
  }

  .header-mark .v-icon {
    font-size: 1.4375rem;
  }

  .header-copy h1 {
    margin-top: 0.1875rem;
    font-size: 1.6875rem;
  }

  .header-copy p {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.55;
  }

  .header-actions {
    width: 100%;
    justify-content: stretch;
  }

  .header-actions ::v-deep .v-btn:not(.v-btn--icon) {
    flex: 1 1 auto;
    min-height: 2.75rem;
  }
}
</style>
